import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  RiCloseLine,
  RiDeleteBinLine,
  RiAddLine,
  RiArrowUpDownLine,
} from '@remixicon/react';
import { addElement } from '../../redux/slices/canvasSlice';

/**
 * Menu Items Modal - Opens beside RightSidebar for editing menu items
 * 
 * Allows editing items, removing items, adding items, and organizing items into sections
 * For matrix mode: Shows subcategories with multiple price fields (one per size)
 */
const MenuItemsModal = ({ 
  open, 
  onClose, 
  items = [], 
  subcategories: initialSubcategories = [],
  sizes: initialSizes = [],
  layout = 'listed',
  sections: initialSections = [],
  currentElementProperties = {},
  currentElementSize = { width: 350, height: 400 },
  currentElementPosition = { x: 100, y: 100 },
  onItemsChange, 
  onSubcategoriesChange,
  onSizesChange,
  onSectionsChange 
}) => {
  const dispatch = useDispatch();
  const [localItems, setLocalItems] = useState([]);
  const [localSubcategories, setLocalSubcategories] = useState([]);
  const [localSizes, setLocalSizes] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedSubcategories, setSelectedSubcategories] = useState(new Set());
  const [sections, setSections] = useState([]);
  
  const isMatrixMode = layout === 'matrix';

  useEffect(() => {
    if (open) {
      if (isMatrixMode) {
        // Ensure items have sizeId matching the sizes
        const processedSubcategories = initialSubcategories ? initialSubcategories.map((sub, index) => {
          const processedItems = (sub.items || []).map((item, i) => {
            // If item already has sizeId, keep it
            if (item.sizeId) {
              return { ...item, id: item.id || `item-${i}` };
            }
            // Try to find matching size by name or by extracting from item name
            let matchingSize = null;
            if (item.size) {
              matchingSize = initialSizes?.find(s => s.name === item.size);
            }
            if (!matchingSize && item.name) {
              // Try to match by checking if size name is in item name
              matchingSize = initialSizes?.find(s => item.name.includes(s.name));
            }
            return {
              ...item,
              id: item.id || `item-${i}`,
              sizeId: matchingSize?.id || null,
              size: item.size || matchingSize?.name || null,
            };
          });
          return {
            ...sub,
            id: sub.id || `subcat-${index}`,
            items: processedItems,
          };
        }) : [];
        setLocalSubcategories(processedSubcategories);
        setLocalSizes(initialSizes || []);
      } else {
        setLocalItems(items ? items.map((item, index) => ({ ...item, id: item.id || `item-${index}` })) : []);
      }
      setSelectedItems(new Set());
      setSelectedSubcategories(new Set());
      setSections(initialSections || []);
    }
  }, [open, items, initialSubcategories, initialSizes, initialSections, isMatrixMode]);

  // Listed mode handlers
  const handleItemChange = (index, field, value) => {
    const updated = [...localItems];
    updated[index] = { ...updated[index], [field]: value };
    setLocalItems(updated);
    if (onItemsChange) {
      onItemsChange(updated);
    }
  };

  const handleRemoveItem = (index) => {
    const updated = localItems.filter((_, i) => i !== index);
    setLocalItems(updated);
    if (onItemsChange) {
      onItemsChange(updated);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      name: 'New Item',
      price: '0.00',
      description: '',
    };
    const updated = [...localItems, newItem];
    setLocalItems(updated);
    if (onItemsChange) {
      onItemsChange(updated);
    }
  };

  // Matrix mode handlers
  const handleSubcategoryChange = (index, field, value) => {
    const updated = [...localSubcategories];
    updated[index] = { ...updated[index], [field]: value };
    setLocalSubcategories(updated);
    if (onSubcategoriesChange) {
      onSubcategoriesChange(updated);
    }
  };

  const handleSubcategoryPriceChange = (subcatIndex, sizeId, price) => {
    const updated = [...localSubcategories];
    const subcat = { ...updated[subcatIndex] };
    const existingItems = subcat.items ? [...subcat.items] : [];
    const size = localSizes.find(s => s.id === sizeId);
    const sizeName = size?.name || '';
    
    // Find item index for this size - try multiple matching strategies
    let itemIndex = existingItems.findIndex(item => {
      // First try exact sizeId match
      if (item.sizeId === sizeId) return true;
      // Then try matching by size name
      if (sizeName && (item.size === sizeName || (item.name && item.name.includes(sizeName)))) {
        return true;
      }
      return false;
    });
    
    if (itemIndex >= 0) {
      // Update existing item price and ensure sizeId is set
      existingItems[itemIndex] = {
        ...existingItems[itemIndex],
        price: price,
        sizeId: sizeId, // Ensure sizeId is set
        size: sizeName, // Ensure size name is set
      };
    } else {
      // Create new item for this size
      const newItem = {
        id: `item-${Date.now()}-${sizeId}`,
        name: sizeName ? `${sizeName} ${subcat.name || ''}`.trim() : (subcat.name || 'Item'),
        price: price,
        sizeId: sizeId,
        size: sizeName,
      };
      existingItems.push(newItem);
    }
    
    subcat.items = existingItems;
    updated[subcatIndex] = subcat;
    
    setLocalSubcategories(updated);
    if (onSubcategoriesChange) {
      onSubcategoriesChange(updated);
    }
  };

  const handleAddSubcategory = () => {
    const newSubcategory = {
      id: `subcat-${Date.now()}`,
      name: 'New Subcategory',
      description: '',
      items: [],
    };
    const updated = [...localSubcategories, newSubcategory];
    setLocalSubcategories(updated);
    if (onSubcategoriesChange) {
      onSubcategoriesChange(updated);
    }
  };

  const handleRemoveSubcategory = (index) => {
    const updated = localSubcategories.filter((_, i) => i !== index);
    setLocalSubcategories(updated);
    if (onSubcategoriesChange) {
      onSubcategoriesChange(updated);
    }
  };

  const handleAddSize = () => {
    const newSize = {
      id: `size-${Date.now()}`,
      name: `Option ${localSizes.length + 1}`,
    };
    const updated = [...localSizes, newSize];
    setLocalSizes(updated);
    if (onSizesChange) {
      onSizesChange(updated);
    }
  };

  const handleSizeChange = (sizeIndex, field, value) => {
    const updated = [...localSizes];
    const oldSize = updated[sizeIndex];
    updated[sizeIndex] = { ...updated[sizeIndex], [field]: value };
    setLocalSizes(updated);
    
    // If the size name changed, update all items in subcategories that reference this size
    if (field === 'name' && oldSize.name !== value) {
      const updatedSubcategories = localSubcategories.map(subcat => ({
        ...subcat,
        items: (subcat.items || []).map(item => {
          // If this item matches the size by sizeId, update its size property
          if (item.sizeId === oldSize.id) {
            return {
              ...item,
              size: value, // Update the size name
              // Update item name if it contains the old size name
              name: item.name?.replace(oldSize.name, value) || item.name,
            };
          }
          return item;
        }),
      }));
      setLocalSubcategories(updatedSubcategories);
      if (onSubcategoriesChange) {
        onSubcategoriesChange(updatedSubcategories);
      }
    }
    
    if (onSizesChange) {
      onSizesChange(updated);
    }
  };

  const handleRemoveSize = (sizeIndex) => {
    const sizeToRemove = localSizes[sizeIndex];
    const updated = localSizes.filter((_, i) => i !== sizeIndex);
    setLocalSizes(updated);
    
    // Remove items with this sizeId from all subcategories
    const updatedSubcategories = localSubcategories.map(subcat => ({
      ...subcat,
      items: (subcat.items || []).filter(item => item.sizeId !== sizeToRemove.id),
    }));
    setLocalSubcategories(updatedSubcategories);
    
    if (onSizesChange) {
      onSizesChange(updated);
    }
    if (onSubcategoriesChange) {
      onSubcategoriesChange(updatedSubcategories);
    }
  };

  const getPriceForSize = (subcat, sizeId) => {
    if (!subcat || !sizeId) return '';
    if (!subcat.items || subcat.items.length === 0) return '';
    
    // Always match by sizeId first (this is the most reliable)
    const item = subcat.items.find(item => item.sizeId === sizeId);
    
    // Return the price if found, otherwise return empty string
    return item?.price || '';
  };

  const handleToggleSelect = (index) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  const handleToggleSubcategorySelect = (index) => {
    const newSelected = new Set(selectedSubcategories);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSubcategories(newSelected);
  };

  const handleMoveToSection = () => {
    if (isMatrixMode) {
      // Matrix mode: move selected subcategories
      if (selectedSubcategories.size === 0) return;
      
      const selectedIndices = Array.from(selectedSubcategories).sort((a, b) => a - b);
      const subcategoriesToMove = selectedIndices.map(i => localSubcategories[i]);
      
      // Remove selected subcategories from main list
      const remainingSubcategories = localSubcategories.filter((_, i) => !selectedSubcategories.has(i));
      setLocalSubcategories(remainingSubcategories);
      
      // Create new menuItem element with selected subcategories
      const newElementProperties = {
        ...currentElementProperties,
        subcategories: subcategoriesToMove,
        layout: 'matrix',
        sizes: localSizes,
        backgroundColor: currentElementProperties.backgroundColor || 'rgba(255, 255, 255, 0.95)',
        nameFontSize: currentElementProperties.nameFontSize || '24px',
        descriptionFontSize: currentElementProperties.descriptionFontSize || '16px',
        priceFontSize: currentElementProperties.priceFontSize || '20px',
        priceColor: currentElementProperties.priceColor || '#C61F2F',
        nameFontFamily: currentElementProperties.nameFontFamily,
        nameFontWeight: currentElementProperties.nameFontWeight,
        nameColor: currentElementProperties.nameColor,
        nameTextAlign: currentElementProperties.nameTextAlign,
        itemGap: currentElementProperties.itemGap,
      };
      
      dispatch(
        addElement({
          type: 'menuItem',
          properties: newElementProperties,
          size: currentElementSize,
          position: {
            x: currentElementPosition.x + 50,
            y: currentElementPosition.y + 50,
          },
        })
      );
      
      setSelectedSubcategories(new Set());
      
      // Update parent with remaining subcategories
      if (onSubcategoriesChange) {
        onSubcategoriesChange(remainingSubcategories);
      }
    } else {
      // Listed mode: move selected items
      if (selectedItems.size === 0) return;
      
      const selectedIndices = Array.from(selectedItems).sort((a, b) => a - b);
      const itemsToMove = selectedIndices.map(i => localItems[i]);
      
      // Remove selected items from main list
      const remainingItems = localItems.filter((_, i) => !selectedItems.has(i));
      setLocalItems(remainingItems);
      
      // Create new menuItem element with selected items
      const newElementProperties = {
        ...currentElementProperties,
        items: itemsToMove,
        layout: 'listed',
        backgroundColor: currentElementProperties.backgroundColor || 'rgba(255, 255, 255, 0.95)',
        nameFontSize: currentElementProperties.nameFontSize || '24px',
        descriptionFontSize: currentElementProperties.descriptionFontSize || '16px',
        priceFontSize: currentElementProperties.priceFontSize || '20px',
        priceColor: currentElementProperties.priceColor || '#C61F2F',
        nameFontFamily: currentElementProperties.nameFontFamily,
        nameFontWeight: currentElementProperties.nameFontWeight,
        nameColor: currentElementProperties.nameColor,
        nameTextAlign: currentElementProperties.nameTextAlign,
        itemGap: currentElementProperties.itemGap,
      };
      
      dispatch(
        addElement({
          type: 'menuItem',
          properties: newElementProperties,
          size: currentElementSize,
          position: {
            x: currentElementPosition.x + 50,
            y: currentElementPosition.y + 50,
          },
        })
      );
      
      setSelectedItems(new Set());
      
      // Update parent with remaining items
      if (onItemsChange) {
        onItemsChange(remainingItems);
      }
    }
  };

  const handleSectionItemChange = (sectionIndex, itemIndex, field, value) => {
    const updated = [...sections];
    updated[sectionIndex].items[itemIndex] = {
      ...updated[sectionIndex].items[itemIndex],
      [field]: value,
    };
    setSections(updated);
    if (onSectionsChange) {
      onSectionsChange(updated);
    }
  };

  const handleRemoveSectionItem = (sectionIndex, itemIndex) => {
    const updated = [...sections];
    updated[sectionIndex].items = updated[sectionIndex].items.filter((_, i) => i !== itemIndex);
    if (updated[sectionIndex].items.length === 0) {
      updated.splice(sectionIndex, 1);
    }
    setSections(updated);
    if (onSectionsChange) {
      onSectionsChange(updated);
    }
  };

  const handleMoveItemBackToMain = (sectionIndex, itemIndex) => {
    const item = sections[sectionIndex].items[itemIndex];
    const updatedItems = [...localItems, item];
    setLocalItems(updatedItems);
    
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items = updatedSections[sectionIndex].items.filter((_, i) => i !== itemIndex);
    if (updatedSections[sectionIndex].items.length === 0) {
      updatedSections.splice(sectionIndex, 1);
    }
    setSections(updatedSections);
    
    if (onItemsChange) {
      onItemsChange(updatedItems);
    }
  };

  const handleSectionNameChange = (sectionIndex, name) => {
    const updated = [...sections];
    updated[sectionIndex].name = name;
    setSections(updated);
    if (onSectionsChange) {
      onSectionsChange(updated);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 320, // Position beside RightSidebar (width: 320)
        top: 64, // Below TopHeaderBar (height: 64)
        width: 400,
        height: 'calc(100vh - 64px)',
        backgroundColor: 'white',
        borderLeft: '1px solid #E0E0E0',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 64px)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Edit Items
        </Typography>
        <IconButton onClick={onClose} size="small">
          <RiCloseLine size={20} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {isMatrixMode ? (
          <>
            {/* Options/Sizes Management */}
            <Box sx={{ marginBottom: '20px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                  Options/Sizes ({localSizes.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<RiAddLine size={16} />}
                  onClick={handleAddSize}
                  sx={{
                    textTransform: 'none',
                    color: '#6B22D5',
                    '&:hover': { backgroundColor: '#F3E8FF' },
                  }}
                >
                  Add Option
                </Button>
              </Box>
              {localSizes.map((size, index) => (
                <Box
                  key={size.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    marginBottom: '8px',
                    padding: '8px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '6px',
                  }}
                >
                  <TextField
                    size="small"
                    value={size.name}
                    onChange={(e) => handleSizeChange(index, 'name', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSize(index)}
                    sx={{ color: '#EF4444' }}
                  >
                    <RiDeleteBinLine size={16} />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Subcategories */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                Items ({localSubcategories.length})
              </Typography>
              <Button
                size="small"
                startIcon={<RiAddLine size={16} />}
                onClick={handleAddSubcategory}
                sx={{
                  textTransform: 'none',
                  color: '#6B22D5',
                  '&:hover': { backgroundColor: '#F3E8FF' },
                }}
              >
                Add Item
              </Button>
            </Box>

            {/* Move to Section Button for Matrix Mode */}
            {selectedSubcategories.size > 0 && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<RiArrowUpDownLine size={20} />}
                onClick={handleMoveToSection}
                sx={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: '#6B22D5',
                  '&:hover': {
                    backgroundColor: '#5B1DB7',
                  },
                }}
              >
                Create New Menu Item ({selectedSubcategories.size} subcategor{selectedSubcategories.size > 1 ? 'ies' : 'y'})
              </Button>
            )}

            {localSubcategories.map((subcategory, subcatIndex) => (
              <Box
                key={subcategory.id}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  border: selectedSubcategories.has(subcatIndex) ? '2px solid #6B22D5' : '1px solid #E0E0E0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '8px' }}>
                  <Checkbox
                    checked={selectedSubcategories.has(subcatIndex)}
                    onChange={() => handleToggleSubcategorySelect(subcatIndex)}
                    size="small"
                    sx={{ padding: 0 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSubcategory(subcatIndex)}
                    sx={{ 
                      marginLeft: 'auto',
                      color: '#EF4444' 
                    }}
                  >
                    <RiDeleteBinLine size={18} />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Subcategory Name"
                  value={subcategory.name || ''}
                  onChange={(e) => handleSubcategoryChange(subcatIndex, 'name', e.target.value)}
                  sx={{ marginBottom: '8px' }}
                />

                {/* Prices for all sizes */}
                {localSizes.map((size) => {
                  // Get the current subcategory from localSubcategories to ensure we have latest data
                  const currentSubcat = localSubcategories[subcatIndex];
                  const price = getPriceForSize(currentSubcat || subcategory, size.id);
                  return (
                    <Box key={`${subcategory.id}-${size.id}`} sx={{ display: 'flex', gap: 1, marginBottom: '8px', alignItems: 'center' }}>
                      <Typography sx={{ width: '80px', fontSize: '12px', color: '#666' }}>
                        {size.name}:
                      </Typography>
                      <TextField
                        key={`price-${subcategory.id}-${size.id}-${price}`}
                        size="small"
                        value={price}
                        onChange={(e) => handleSubcategoryPriceChange(subcatIndex, size.id, e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: '#666', fontSize: '12px' }}>£</Typography>,
                        }}
                      />
                    </Box>
                  );
                })}

                <TextField
                  fullWidth
                  size="small"
                  label="Subcategory Description"
                  value={subcategory.description || ''}
                  onChange={(e) => handleSubcategoryChange(subcatIndex, 'description', e.target.value)}
                  multiline
                  rows={2}
                  sx={{ marginTop: '8px' }}
                />
              </Box>
            ))}

            {localSubcategories.length === 0 && (
              <Box sx={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Typography>No subcategories yet. Click "Add Subcategory" to get started.</Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Listed Mode - Simple Items List */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RiAddLine size={20} />}
              onClick={handleAddItem}
              sx={{
                marginBottom: '16px',
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#E0E0E0',
                color: '#000',
                '&:hover': {
                  borderColor: '#6B22D5',
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              Add Item
            </Button>

            {/* Move to Section Button */}
            {selectedItems.size > 0 && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<RiArrowUpDownLine size={20} />}
                onClick={handleMoveToSection}
                sx={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: '#6B22D5',
                  '&:hover': {
                    backgroundColor: '#5B1DB7',
                  },
                }}
              >
                Create New Menu Item ({selectedItems.size} item{selectedItems.size > 1 ? 's' : ''})
              </Button>
            )}

            {/* Main Items List */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '12px', color: '#666' }}>
              Items ({localItems.length})
            </Typography>

            {localItems.map((item, index) => (
              <Box
                key={item.id || index}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '12px',
                  border: selectedItems.has(index) ? '2px solid #6B22D5' : '1px solid #E0E0E0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, marginBottom: '8px' }}>
                  <Checkbox
                    checked={selectedItems.has(index)}
                    onChange={() => handleToggleSelect(index)}
                    size="small"
                    sx={{ padding: 0 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveItem(index)}
                    sx={{
                      marginLeft: 'auto',
                      color: '#EF4444',
                      '&:hover': { backgroundColor: '#FEE2E2' },
                    }}
                  >
                    <RiDeleteBinLine size={18} />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Item Name"
                  value={item.name || ''}
                  onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                  sx={{ marginBottom: '8px' }}
                />

                <Box sx={{ display: 'flex', gap: 1, marginBottom: '8px' }}>
                  <TextField
                    size="small"
                    label="Price"
                    value={item.price || ''}
                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>£</Typography>,
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={item.description || ''}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  multiline
                  rows={2}
                />
              </Box>
            ))}

            {localItems.length === 0 && sections.length === 0 && (
              <Box sx={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Typography>No items yet. Click "Add Item" to get started.</Typography>
              </Box>
            )}
          </>
        )}

        {/* Sections (only for listed mode) */}
        {!isMatrixMode && sections.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '12px', color: '#666' }}>
              Sections ({sections.length})
            </Typography>

            {sections.map((section, sectionIndex) => (
              <Box
                key={section.id}
                sx={{
                  backgroundColor: '#F0F0F0',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  border: '1px solid #E0E0E0',
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  value={section.name}
                  onChange={(e) => handleSectionNameChange(sectionIndex, e.target.value)}
                  sx={{
                    marginBottom: '12px',
                    '& .MuiInputBase-root': {
                      fontWeight: 600,
                      fontSize: '14px',
                    },
                  }}
                />

                {section.items.map((item, itemIndex) => (
                  <Box
                    key={item.id || itemIndex}
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      padding: '10px',
                      marginBottom: itemIndex < section.items.length - 1 ? '8px' : 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveItemBackToMain(sectionIndex, itemIndex)}
                        sx={{
                          color: '#6B22D5',
                          '&:hover': { backgroundColor: '#F3E8FF' },
                        }}
                        title="Move back to main list"
                      >
                        <RiArrowUpDownLine size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSectionItem(sectionIndex, itemIndex)}
                        sx={{
                          color: '#EF4444',
                          '&:hover': { backgroundColor: '#FEE2E2' },
                        }}
                      >
                        <RiDeleteBinLine size={16} />
                      </IconButton>
                    </Box>

                    <TextField
                      fullWidth
                      size="small"
                      label="Item Name"
                      value={item.name || ''}
                      onChange={(e) => handleSectionItemChange(sectionIndex, itemIndex, 'name', e.target.value)}
                      sx={{ marginBottom: '8px' }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, marginBottom: '8px' }}>
                      <TextField
                        size="small"
                        label="Price"
                        value={item.price || ''}
                        onChange={(e) => handleSectionItemChange(sectionIndex, itemIndex, 'price', e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>£</Typography>,
                        }}
                      />
                    </Box>

                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={item.description || ''}
                      onChange={(e) => handleSectionItemChange(sectionIndex, itemIndex, 'description', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}

      </Box>
    </Box>
  );
};

export default MenuItemsModal;

